import React from "react";
import { EMPTY } from "../../../../utils/perfFormat";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";

// Small pieces the athlete performance cards share.

export const cx = (...parts) => parts.filter(Boolean).join(" ");

// Phones: a stat's ranks as text, the phone box score's way (MobileStatCell,
// Figma 2020:1829): "Top 12% · Med 19.2", each kept whole, a narrow cell
// wrapping between them, never inside one. `ranks` are strings or
// { text, approximate } (≈, read out as "approximate"); empty ones are
// skipped, and nothing renders without one.
export const RankLine = ({ ranks, className = "" }) => {
  const shown = (Array.isArray(ranks) ? ranks : [])
    .map((r) => (typeof r === "string" ? { text: r } : r))
    .filter((r) => r && r.text);
  if (!shown.length) return null;
  return (
    <span className={cx("bm-body-xs text-bm-muted", className)}>
      {shown.map((r, i) => (
        <React.Fragment key={`${r.text}-${i}`}>
          {i > 0 && " · "}
          <span className="whitespace-nowrap">
            {r.text}
            {r.approximate && (
              <>
                <span aria-hidden="true"> ≈</span>
                <span className="sr-only"> approximate</span>
              </>
            )}
          </span>
        </React.Fragment>
      ))}
    </span>
  );
};

// A stat with its chips: the profile's StatTile on desktop (16px corners,
// centred), the phone stat cell on phones (12px, left aligned on the
// subtle fill). `children` are the chips (RankChip, MedianChip); on phones
// `ranks` (RankLine) prints them as text instead, as the phone box score
// does, so one tab never mixes chips and text ranks. `note` is a line under
// them ("lower is better").
export const PerfTile = ({ value, label, children, ranks, note, className = "" }) => {
  const phone = usePhoneTheme();
  if (phone) {
    return (
      <div className={cx("flex min-w-0 flex-col gap-1 break-words rounded-[12px] bg-bm-subtle p-3", className)}>
        <span className="bm-num-ml text-bm-ink">{value ?? EMPTY}</span>
        <span className="bm-overline">{label}</span>
        {ranks ? <RankLine ranks={ranks} /> : children && <span className="flex flex-wrap gap-1 pt-1">{children}</span>}
        {note && <span className="bm-body-xs text-bm-muted">{note}</span>}
      </div>
    );
  }
  return (
    <div className={cx("flex min-w-0 flex-col items-center gap-2 rounded-[16px] border border-bm-border bg-white px-2 py-4 text-center", className)}>
      <span className="bm-num-ml text-bm-ink">{value ?? EMPTY}</span>
      <span className="bm-overline">{label}</span>
      {children && <span className="flex flex-wrap items-center justify-center gap-[6px]">{children}</span>}
      {note && <span className="bm-body-s text-bm-muted">{note}</span>}
    </div>
  );
};

// Phones: a card's legend as plain lines under its cells (the phone box
// score's RankLegend look), since the phone cells print their ranks as text
// and have no chip colours for legend dots to name.
export const PhoneLegend = ({ lines, className = "" }) => {
  const shown = (Array.isArray(lines) ? lines : []).filter(Boolean);
  if (!shown.length) return null;
  return (
    <div className={cx("flex flex-col gap-1", className)}>
      {shown.map((line) => (
        <p key={line} className="bm-body-xs text-bm-muted">
          {line}
        </p>
      ))}
    </div>
  );
};

// A short muted note in a card, saying why the card holds what it holds
// (a camp's games, games the team entered, the profile's radar in the
// Player Archetype's place): bm-body-s, the phone cards' caption type
// (bm-body-xs) on phones. Nothing without text.
export const CardNote = ({ children, className = "" }) => {
  const phone = usePhoneTheme();
  if (!children) return null;
  return <p className={cx("break-words text-bm-muted", phone ? "bm-body-xs" : "bm-body-s", className)}>{children}</p>;
};

// Keeps a keyboard or screen reader user's place when the card in a slot
// gives way to another (`swap` names what the slot holds: the Player
// Archetype's shell giving way to the profile's radar when the group being
// built never came, notes 10/21 D4 c): focus that was inside the old card,
// now gone with it, moves to the new card's toggle instead of falling back
// to the top of the page. The wrapper has no box of its own (display:
// contents), so the section's layout is unchanged. A class for
// getSnapshotBeforeUpdate: it reads the focus before the old card leaves.
const FOCUSABLE = "button[aria-expanded], button:not([disabled]), [href], select, [tabindex]:not([tabindex='-1'])";
export class KeepFocus extends React.Component {
  constructor(props) {
    super(props);
    this.ref = React.createRef();
  }

  getSnapshotBeforeUpdate(prev) {
    const slot = this.ref.current;
    return prev.swap !== this.props.swap && Boolean(slot && slot.contains(document.activeElement));
  }

  componentDidUpdate(prev, state, hadFocus) {
    const slot = this.ref.current;
    const active = document.activeElement;
    if (!hadFocus || !slot || (active && active !== document.body)) return;
    const target = slot.querySelector(FOCUSABLE);
    if (target) target.focus();
  }

  render() {
    return (
      <div ref={this.ref} className="contents">
        {this.props.children}
      </div>
    );
  }
}

// A game's result pill is the shared ForBrands/ui/ResultPill (B18: a team
// win in bm-orange, a loss grey, as the bars), the coach Game Log's too.

// A sub heading inside a card (a skill group, "vs. teammates").
export const SubHead = ({ children, as: Tag = "h3", className = "" }) => <Tag className={cx("m-0 bm-overline", className)}>{children}</Tag>;
